import { useMemo, useState } from 'react';
import { P2GBox, P2GCollapse, P2GGrid, P2GLink, P2GText } from '@parcel2go/core-ui';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import { AppCard } from '~/components/card/app-card';
import SelectOrCopy from '~/apps/smartsend/components/smartsend/item/SelectOrCopy';
import { toDisplayCurrency } from '~/components/currency/toDisplayCurrency';
import { groupByDate } from '~/lib/utils/array';
import { useMeasurementSystem } from '~/hooks/use-measurement-system';
import { formatWeightString, formatDimensionsString } from '~/apps/smartsend/lib/helpers/DimensionUnits';
import { ArchivedOrder } from './types';
import { ArchivedOrderDetails } from './ArchivedOrderDetails';
import { ArchivedOrderCard } from './ArchivedOrderCard';

interface props {
  orders: ArchivedOrder[];
  isXs: boolean;
}

const ArchivedOrderRow = ({ order }: { order: ArchivedOrder }) => {
  const { t } = useTranslation(TranslationNamespaces.MYORDERS);
  const { unit } = useMeasurementSystem();
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <AppCard>
      <P2GBox p={2}>
        <P2GGrid container spacing={2}>
          <P2GGrid item xs={12} sm={4}>
            <P2GText component="div" fontWeight="bold">
              {order.Description}
            </P2GText>
            <P2GText component="div" variant="body2">
              {formatWeightString(order.Weight, unit)} |{' '}
              {formatDimensionsString({ length: order.Length, width: order.Width, height: order.Height }, unit)}
            </P2GText>
            <P2GText component="div" variant="body2">
              {t('ARCHIVED_ORDER_VALUE', {
                defaultValue: 'Value: {{value}}',
                value: toDisplayCurrency(undefined, order.Value)
              })}
            </P2GText>
          </P2GGrid>
          <P2GGrid item xs={12} sm={3}>
            <P2GText component="div" fontWeight="bold">
              {order.DeliveryName}
            </P2GText>
            <P2GText component="div" variant="body2">
              {order.DeliveryAddressLine1}
            </P2GText>
            <P2GText component="div" variant="body2">
              {order.DeliveryTown}, {order.DeliveryPostcode}
            </P2GText>
          </P2GGrid>
          <P2GGrid item xs={12} sm={3}>
            <P2GText component="div" fontWeight="bold">
              {order.ServiceName}
            </P2GText>
            <P2GBox display="flex" alignItems="center" gap={1}>
              <P2GText component="span" variant="body2">
                {t('ARCHIVED_ORDERS_REFERENCE_PREFIX', {
                  defaultValue: 'Ref: {{reference}}',
                  reference: order.ReferenceNumber
                })}
              </P2GText>
              <SelectOrCopy code={order.ReferenceNumber} variant="body2" />
            </P2GBox>
            {order.TrackingNumber && (
              <P2GBox display="flex" alignItems="center" gap={1}>
                <P2GText component="span" variant="body2">
                  {t('ARCHIVED_ORDERS_TRACKING_PREFIX', {
                    defaultValue: 'Tracking: {{tracking}}',
                    tracking: order.TrackingNumber
                  })}
                </P2GText>
                <SelectOrCopy code={order.TrackingNumber} variant="body2" />
                {order.TrackingUrl && (
                  <P2GLink href={order.TrackingUrl} target="_blank" variant="body2">
                    {t('ARCHIVED_ORDERS_TRACK_LINK', { defaultValue: 'Track' })}
                  </P2GLink>
                )}
              </P2GBox>
            )}
            <P2GText component="div" variant="body2">
              {t('ARCHIVED_ORDERS_COLUMN_TOTAL', { defaultValue: 'Total' })}:{' '}
              {toDisplayCurrency(undefined, order.TotalIncVat)}
            </P2GText>
          </P2GGrid>
          <P2GGrid item xs={12} sm={2}>
            <P2GLink onClick={() => setIsExpanded((current) => !current)}>
              {isExpanded
                ? t('ARCHIVED_ORDERS_HIDE_LINK', { defaultValue: 'Hide details' })
                : t('ARCHIVED_ORDERS_VIEW_MORE_DETAILS_LINK', { defaultValue: 'View details' })}
            </P2GLink>
          </P2GGrid>
        </P2GGrid>
        <P2GCollapse in={isExpanded} unmountOnExit>
          <ArchivedOrderDetails order={order} />
        </P2GCollapse>
      </P2GBox>
    </AppCard>
  );
};

export const ArchivedOrdersTable: React.FC<props> = ({ orders, isXs }) => {
  const { t } = useTranslation(TranslationNamespaces.MYORDERS);
  const groups = useMemo(() => groupByDate(orders, (order) => order.CollectionDate ?? order.ImportedDate), [orders]);

  if (isXs) {
    return (
      <P2GBox display="flex" flexDirection="column" gap={2}>
        {orders.map((order) => (
          <ArchivedOrderCard key={order.Id} order={order} />
        ))}
      </P2GBox>
    );
  }

  return (
    <P2GBox display="flex" flexDirection="column" gap={2}>
      <AppCard>
        <P2GBox p={2}>
          <P2GGrid container spacing={2}>
            <P2GGrid item sm={4}>
              <P2GText fontWeight="bold">
                {t('ARCHIVED_ORDERS_COLUMN_ORDER_DETAILS', { defaultValue: 'Order Details' })}
              </P2GText>
            </P2GGrid>
            <P2GGrid item sm={3}>
              <P2GText fontWeight="bold">
                {t('ARCHIVED_ORDERS_COLUMN_RECIPIENT', { defaultValue: 'Recipient' })}
              </P2GText>
            </P2GGrid>
            <P2GGrid item sm={3}>
              <P2GText fontWeight="bold">
                {t('ARCHIVED_ORDERS_COLUMN_SERVICE_REFERENCE', { defaultValue: 'Service / Reference' })}
              </P2GText>
            </P2GGrid>
            <P2GGrid item sm={2}>
              <P2GText fontWeight="bold">{t('ARCHIVED_ORDERS_COLUMN_ACTIONS', { defaultValue: 'Actions' })}</P2GText>
            </P2GGrid>
          </P2GGrid>
        </P2GBox>
      </AppCard>
      {groups.map((group) => (
        <P2GBox key={group.title}>
          <P2GBox mb={1}>
            <P2GText fontWeight="bold">
              {group.title} ({group.items.length})
            </P2GText>
          </P2GBox>
          <P2GBox display="flex" flexDirection="column" gap={2}>
            {group.items.map((order) => (
              <ArchivedOrderRow key={order.Id} order={order} />
            ))}
          </P2GBox>
        </P2GBox>
      ))}
    </P2GBox>
  );
};
